import { useState, useEffect, useCallback } from "react";
import styles from "./DeleteProjectForm.module.css";
import Button from "../../../components/UI/Button/Button";
import FeedbackModal from "../../../components/UI/FeedbackModal/FeedbackModal";
import { getAllProjects, deleteProject } from "../../../services/client-project-service";

export default function DeleteProjectForm() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [modal, setModal] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const openModal = useCallback(({ type, title, message }) => {
    setModal({ open: true, type, title, message });
  }, []);

  const loadProjects = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getAllProjects();
      if (result.success) {
        setProjects(result.data || []);
      } else {
        openModal({
          type: "error",
          title: "Error",
          message: result.error || "Failed to load projects",
        });
      }
    } catch (error) {
      openModal({
        type: "error",
        title: "Error",
        message: "Failed to load projects",
      });
    } finally {
      setIsLoading(false);
    }
  }, [openModal]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleProjectSelect = (e) => {
    const projectId = e.target.value;
    const project = projects.find((p) => p._id === projectId);
    setSelectedProject(project || null);
    setConfirmDelete(false);
  };

  const handleDelete = async () => {
    if (!selectedProject) {
      openModal({
        type: "error",
        title: "Error",
        message: "Please select a project to delete",
      });
      return;
    }

    if (!confirmDelete) {
      openModal({
        type: "error",
        title: "Confirmation Required",
        message: "Please confirm deletion by checking the confirmation box",
      });
      return;
    }

    setIsDeleting(true);

    try {
      const result = await deleteProject(selectedProject._id);

      if (result.success) {
        openModal({
          type: "success",
          title: "Success",
          message: `Project "${selectedProject.title || selectedProject._id}" deleted successfully!`,
        });
        await loadProjects();
        setSelectedProject(null);
        setConfirmDelete(false);
      } else {
        throw new Error(result.error || "Failed to delete project");
      }
    } catch (error) {
      openModal({
        type: "error",
        title: "Error",
        message: error.message || "Failed to delete project",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteButtonClassName = [
    styles.deleteButton,
    confirmDelete && selectedProject ? styles.deleteButtonDanger : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <div className={styles.projectForm}>
        <div className={styles.formGroup}>
          <label htmlFor="projectSelect" className={styles.label}>
            Select Project to Delete *
          </label>
          <select
            id="projectSelect"
            name="projectSelect"
            value={selectedProject?._id || ""}
            onChange={handleProjectSelect}
            className={styles.select}
            required
            disabled={isLoading || isDeleting}
          >
            <option value="">
              {isLoading ? "Loading projects..." : "Select a project..."}
            </option>
            {projects.map((project) => (
              <option key={project._id} value={project._id}>
                {project.title || project._id}
              </option>
            ))}
          </select>
        </div>

        {selectedProject && (
          <div className={styles.formGroup}>
            <div className={styles.projectDetails}>
              <h3 className={styles.projectDetailsTitle}>
                Project Details:
              </h3>
              <p className={styles.projectDetailsLine}>
                <strong>Title:</strong> {selectedProject.title || "N/A"}
              </p>
              <p className={styles.projectDetailsLine}>
                <strong>Type:</strong> {selectedProject.type || "N/A"}
              </p>
              {selectedProject.description && (
                <p className={styles.projectDetailsLine}>
                  <strong>Description:</strong> {selectedProject.description.substring(0, 100)}
                  {selectedProject.description.length > 100 ? "..." : ""}
                </p>
              )}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.confirmLabel}>
                <input
                  type="checkbox"
                  checked={confirmDelete}
                  onChange={(e) => setConfirmDelete(e.target.checked)}
                  disabled={isDeleting}
                  className={styles.confirmCheckbox}
                />
                <span className={styles.confirmText}>
                  I confirm that I want to delete this project. This action cannot be undone.
                </span>
              </label>
            </div>
          </div>
        )}

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="default"
            onClick={handleDelete}
            disabled={!selectedProject || !confirmDelete || isDeleting || isLoading}
            className={deleteButtonClassName}
          >
            {isDeleting ? "Deleting..." : "Delete Project"}
          </Button>
        </div>
      </div>

      <FeedbackModal
        open={modal.open}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText="Close"
      />
    </>
  );
}

